import Script from 'next/script';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from 'context-providers/user-provider';
import {
  useFeatureFlags,
  ExperimentFlag,
} from 'context-providers/feature-flag-provider';
import { apiGetChatToken } from 'lib/api';
import { User } from 'entities/user';

const shouldRenderChatWidget = (
  router: ReturnType<typeof useRouter>,
  userState: User | null,
  chatToken: string | null,
  isDigitalGeniusChatEnabled: boolean,
): boolean => {
  // Don't render if widget ID is not configured
  if (!process.env.NEXT_PUBLIC_DG_CHAT_WIDGET_ID) {
    return false;
  }

  // Don't render if feature flag is disabled
  if (!isDigitalGeniusChatEnabled) {
    return false;
  }

  // Only render chat widget on account pages when user is logged in
  // Do not render on /account/home or /account/refer pages

  const notLoggedInOnAccountPages =
    !router.pathname.startsWith('/account') || !userState;

  const isHomeJourneyOrReferPage =
    router.pathname.startsWith('/account/home') ||
    router.pathname.startsWith('/account/journey') ||
    router.pathname === '/account/refer';

  if (notLoggedInOnAccountPages || isHomeJourneyOrReferPage) {
    return false;
  }

  // Don't render until we have the chat token
  if (!chatToken) {
    return false;
  }

  return true;
};

const ChatWidget = () => {
  const router = useRouter();
  const { userState } = useContext(UserContext);
  const { experimentFlags } = useFeatureFlags();
  const [chatToken, setChatToken] = useState<string | null>(null);

  // Extract the flag value explicitly so React tracks it as a dependency
  const isDigitalGeniusChatEnabled =
    experimentFlags[ExperimentFlag.DigitalGeniusChat];

  // Fetch chat token once when component mounts
  useEffect(() => {
    // Only fetch if user is logged in and on account pages
    if (!userState || !router.pathname.startsWith('/account')) {
      return;
    }

    const fetchToken = async () => {
      try {
        const response = await apiGetChatToken();
        if (response?.token) {
          setChatToken(response.token);
        }
      } catch (error) {
        // Silently fail - widget won't render without token
        console.error('Failed to fetch chat token:', error);
      }
    };

    fetchToken();
  }, [userState, router.pathname]);

  useEffect(() => {
    // When user logs out (userState becomes null), destroy the chat
    if (!userState && (window as any).dgchat) {
      (window as any).dgchat?.methods?.resetChat?.();
      setChatToken(null);
    }
  }, [userState]);

  // Determine if chat widget should be rendered
  const shouldRender = shouldRenderChatWidget(
    router,
    userState,
    chatToken,
    isDigitalGeniusChatEnabled,
  );

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <Script
        id='dg-chat-config'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
            window.DG_CHAT_WIDGET_CONFIG = {
              "widgetId": "${process.env.NEXT_PUBLIC_DG_CHAT_WIDGET_ID}",
              "env": "us",
              "metadata": {
                "auth_token": "${chatToken}"
              }
            };
          `,
        }}
      />
      <Script
        id='dg-chat-init'
        strategy='afterInteractive'
        src='https://chat.digitalgenius.com/init.js'
        onLoad={() => {
          if ((window as any).dgchat) {
            (window as any).dgchat.init();
          }
        }}
      />
    </>
  );
};

export default ChatWidget;
